import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import {
  useCopyScheduledPrompt, useListAddressableInternalUsers
} from '../../../../../api/denaliAssistant'
import { decipherError } from '../../../../../utils/decipherError'
import SydModalActions from '../../../../../components/commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../components/commonDesign/Button'
import Loading from '../../../../../components/molecules/Loading'
import SydLabel, { hookFormErrorAdapter } from '../../../../../components/commonDesign/SydLabel'
import MiniTable from '../../../../../components/pages/admin/shared/MiniTable'
import ScrollContent from '../../../../../components/molecules/ScrollContent'
import SydInput from '../../../../../components/commonDesign/SydInput'
import FlexRow from '../../../../../components/molecules/FlexRow'

const useSubmitter = (form, scheduleId, isDigest, onComplete) => {
  const { handleSubmit, clearErrors } = form
  const { mutateAsync: copyPrompt, isLoading: isCopying } = useCopyScheduledPrompt()

  const isLoading = useMemo(() => {
    return isCopying
  }, [isCopying])

  const onSubmit = useCallback(async (formData) => {
    const command = {
      scheduleId,
      userIds: [formData.userId]
    }

    try {
      form?.clearErrors?.()
      const result = await copyPrompt(command)
      onComplete(result)
    } catch (err) {
      form.setError('root', {
        type: 'custom',
        message: decipherError(err) || `Something unexpected happened while copying the scheduled ${isDigest ? 'digest' : 'prompt'}.`
      })
    }
  }, [form, scheduleId, onComplete, copyPrompt, isDigest])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }
    clearErrors()
    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, clearErrors, onSubmit])

  return {
    submitter,
    processing: isLoading
  }
}
const COLUMN_CONFIG = [
  { accessor: 'userId', header: 'User ID' },
  { accessor: 'email', header: 'Email' },
  { accessor: 'firstName', header: 'First' },
  { accessor: 'lastName', header: 'Last' }
]

function CopyScheduleInternalForm ({ schedule, onCancel, onComplete }) {
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      userId: ''
    }
  })
  const { submitter, processing } = useSubmitter(form, schedule?.scheduleId, schedule.isDigest, onComplete)
  const { data: users, isLoading } = useListAddressableInternalUsers()
  const [search, setSearch] = useState('')
  const filteredUsers = useMemo(() => {
    return (users?.users || []).filter(u => {
      const email = u.email.includes(search)
      const fn = u.firstName.includes(search)
      const ln = u.lastName.includes(search)
      return email || fn || ln
    })
  }, [users, search])

  if (isLoading) {
    return (
      <Loading />
    )
  }

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          This will copy the scheduled prompt/digest to another user
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='userId'
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <SydLabel label='User who will receive a copy' required error={hookFormErrorAdapter(form, fieldState)}>
                  <FlexRow className='mb-1'>
                    <SydInput
                      value={search}
                      iconName='search'
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </FlexRow>
                  <ScrollContent maxHeight='20vh' style={{ border: 'var(--dai-border1)' }} className='mb-3'>
                    <MiniTable
                      data={filteredUsers}
                      columns={COLUMN_CONFIG}
                      keyAccessor={k => k?.userId}
                      onSelect={(row) => field.onChange({ target: { value: row.userId } })}
                      selected={(users?.users || []).find(x => x.userId === field.value)}
                    />
                  </ScrollContent>
                </SydLabel>
                <div className='p-md' style={{ border: 'var(--dai-border1)' }}>{(users?.users || []).find(x => x.userId === field.value)?.email || 'Please make a selection'}</div>
              </>
            )}
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {/* {error && <div className='__error'>{error}</div>} */}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              icon='copy'
              size='md'
              onClick={submitter}
            >
              Copy {schedule?.isDigest ? 'Digest' : 'Scheduled Prompt'}
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

CopyScheduleInternalForm.propTypes = {
  schedule: PropTypes.shape({
    scheduleId: PropTypes.string.isRequired,
    isDigest: PropTypes.bool.isRequired
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default CopyScheduleInternalForm
